import { CREDIT_LOSS_PERCENT_RANGE, OCCUPANCY_PERCENT_RANGE, dateStringSchema, incomeClassSchema, incomeCommitmentDraftSchema, incomeFundSchema, taxonomyRefSchema, } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
export const incomeOwnershipInput = z.object({ ownershipDisplayId: displayId });
const unitBillLine = z.object({
    taxonomy: taxonomyRefSchema.nullable(),
    description: z.string(),
    monthlyAmount: z.number(),
});
const associationSummary = z.object({
    kind: z.literal('association'),
    units: z.array(z.object({
        unitDisplayId: displayId,
        label: z.string(),
        owner: z.string().nullable(),
        lines: z.array(unitBillLine),
    })),
    totals: z.object({
        commonCharges: z.number(),
        specialAssessments: z.number(),
        otherUnitCharges: z.number(),
    }),
    lines: z.array(unitBillLine),
    issues: z.array(z.string()),
});
const rentBasis = z.object({
    monthlyAmount: z.number().nullable(),
    source: z.enum(['active_schedule', 'last_lease', 'market_rent', 'missing']),
});
const occupancy = z.object({
    derivedPercent: z.number(),
    overridePercent: z.number().nullable(),
    effectivePercent: z.number(),
});
const rentalSummary = z.object({
    kind: z.literal('rental'),
    units: z.array(z.object({
        unitDisplayId: displayId,
        label: z.string(),
        tenant: z.string().nullable(),
        leased: z.boolean(),
        leaseEnds: dateStringSchema.nullable(),
        rentBasis,
        taxonomy: taxonomyRefSchema,
    })),
    grossPotentialRent: z.number(),
    occupancy,
    occupiedRent: z.number(),
    creditLoss: z.object({ percent: z.number().nullable(), monthlyAmount: z.number() }),
});
export const incomeCommitmentSummaryOutput = z.discriminatedUnion('kind', [
    associationSummary,
    rentalSummary,
]);
export const setOccupancyOverrideSchema = z.object({
    ownershipDisplayId: displayId,
    percent: z
        .number()
        .min(OCCUPANCY_PERCENT_RANGE.min)
        .max(OCCUPANCY_PERCENT_RANGE.max)
        .nullable()
        .describe("The occupancy percent the forecast uses, from 0 to 100. Null clears it, so the forecast uses today's occupancy."),
});
export const occupancyOutput = occupancy;
export const setCreditLossSchema = z.object({
    ownershipDisplayId: displayId,
    percent: z
        .number()
        .min(CREDIT_LOSS_PERCENT_RANGE.min)
        .max(CREDIT_LOSS_PERCENT_RANGE.max)
        .nullable()
        .describe('The percent of the rent that is billed but not collected, from 0 to 100. Null clears it.'),
});
export const creditLossOutput = rentalSummary.shape.creditLoss;
export const incomeRunRateOutput = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('unavailable') }),
    z.object({ kind: z.literal('empty') }),
    z.object({
        kind: z.literal('rates'),
        fromMonth: z.string(),
        toMonth: z.string(),
        monthsIncluded: z.number(),
        lines: z.array(z.object({ taxonomyId: z.number(), monthlyAmount: z.number() })),
        covered: z.array(z.object({
            taxonomyId: z.number(),
            monthlyAmount: z.number(),
            reason: z.literal('unit_billing'),
        })),
        unmatched: z.array(z.object({ accountName: z.string(), monthlyAmount: z.number() })),
    }),
]);
export const importIncomeRunRatesSchema = z.object({
    ownershipDisplayId: displayId,
    lines: z
        .array(z.object({ taxonomy: taxonomyRefSchema, monthlyAmount: z.number().positive() }))
        .min(1)
        .max(200),
});
export const importIncomeRunRatesOutput = z.object({ created: z.number() });
export const createIncomeCommitmentSchema = z.object({
    ownershipDisplayId: displayId,
    draft: incomeCommitmentDraftSchema,
});
export const updateIncomeCommitmentSchema = z.object({
    displayId,
    draft: incomeCommitmentDraftSchema,
});
export const deleteIncomeCommitmentSchema = z.object({ displayId });
export const incomeCommitmentRowSchema = z.object({
    displayId,
    status: z.string(),
    draft: incomeCommitmentDraftSchema.nullable(),
    runRate: z
        .object({
        taxonomy: taxonomyRefSchema,
        monthlyAmount: z.number(),
        startDate: dateStringSchema,
        incomeClass: incomeClassSchema,
        fund: incomeFundSchema,
    })
        .nullable(),
});
export const listIncomeCommitmentsOutput = z.array(incomeCommitmentRowSchema);
//# sourceMappingURL=incomeCommitments.js.map